/**
 * SIMS-8687 AC2 (PRD FR-21) — action-code normalisation and the uniqueness comparison.
 *
 * FR-21: "An action's code cannot change after creation; codes are unique, uppercase, no spaces."
 * AC2: "when I enter a code in lower case, with spaces, or matching an existing action, then it is
 * upper-cased and stripped of spaces, and a duplicate is refused with a message."
 *
 * TWO FUNCTIONS, BECAUSE THE SOURCES CONFLICT ON ONE POINT AND BOTH READINGS MATTER.
 *
 * `normaliseActionCode` is what gets STORED: trimmed, inner whitespace turned into underscores,
 * upper-cased. `action one` becomes `ACTION_ONE`, and an underscore the user typed is theirs
 * to keep.
 *
 * `actionCodeKey` is what uniqueness COMPARES, and it folds separators too. That is needed because
 * TestRail C17715 requires BOTH `'action one'` AND `'Action_One'` to collide with an existing
 * `ACTION_ONE`, and a separator is exactly what must not decide that. All three key to
 * `ACTIONONE`, so the same permission cannot enter twice in different spellings — the point
 * C17715 is making ("Normalise-then-compare, or the same code enters twice in different
 * spellings").
 *
 * SIMS-9122 SETTLED THE SEPARATOR, which this file previously carried as an open PO question.
 * PRD v0.3 FR-21 is the binding text — "Codes are unique, uppercase, and contain no spaces" —
 * and it rules out keeping the space, but says nothing about what replaces it. Both vanishing
 * and an underscore satisfy it; the developer chose the underscore, because every code the
 * product actually holds is UPPER_SNAKE_CASE (WEB_ACTION_ADD, ACCOUNT_VIEW, AGNOS_PART_VIEW)
 * and `VIEWREPORTS` would have been the only one without separators.
 *
 * Note what the SIMS-9122 ticket's own Expected field says — `" lower case code "` becoming
 * `"LOWER CASE CODE"`, spaces intact. That contradicts FR-21 and is not implemented. It reads
 * as AC2's "upper-cased and stripped of spaces" being taken as "trimmed"; the word does double
 * duty and the wrong sense was written into the example.
 *
 * `actionCodeKey` did NOT change for any of this, which is the property to preserve: the
 * comparison has always been insensitive to which separator the stored form uses.
 */

/**
 * The stored form: trimmed, inner whitespace collapsed to single underscores, upper-cased
 * (AC2 / FR-21).
 *
 * Trim FIRST — collapsing before trimming would turn a leading space into a leading underscore
 * and " " into "_", inventing a code out of nothing where the AC asks for none.
 */
export const normaliseActionCode = (raw: string): string =>
	raw.trim().replace(/\s+/g, "_").toUpperCase();

/**
 * The uniqueness key: the stored form with separators folded away, so two codes that differ only
 * by case or by where the separators fall are the same code. See the note above.
 */
export const actionCodeKey = (raw: string): string =>
	normaliseActionCode(raw).replace(/[_\-.]/g, "");

/**
 * The action already using `candidate`, or undefined.
 *
 * `excludeId` skips the record being edited — without it, saving an existing action would find
 * itself and report its own code as a duplicate. (The code is immutable on edit per AC3, so this
 * is belt and braces rather than a live path, but a validator that reports a false conflict is
 * worse than one that never runs.)
 */
export const findActionCodeConflict = <T extends { _id: string; code: string }>(
	actions: T[],
	candidate: string,
	excludeId?: string,
): T | undefined => {
	const key = actionCodeKey(candidate);
	if (key === "") {
		return undefined;
	}
	return actions.find(
		(action) => action._id !== excludeId && actionCodeKey(action.code) === key,
	);
};
